/**
 * Financials Provenance Schema (Issue #1519)
 *
 * Single Zod source of truth for the JSON shapes that carry borrower
 * underwriting signals across the platform:
 *
 *   1. `BorrowingEntity.financialsSnapshot`  — cached underwriting
 *      signals updated by event listeners (credit-report extraction,
 *      PFS extraction, BorrowerExit closures). Driven by Issue #1483
 *      Gap 8 / #1469 v2.
 *
 *   2. `Case.tierReasonContext.guarantorTiers[].inputs` — raw inputs
 *      the borrower-tier classifier saw for each guarantor. Captured
 *      verbatim so engineers can debug "why is this borrower Tier 3"
 *      without consulting another system. Driven by Issue #1466.
 *
 * Both shapes embed PII (FICO, liquidity, net worth) — the audit
 * sanitiser in `audit/audit-utils.ts` redacts these field names before
 * payloads reach Loki / audit storage.
 *
 * **Why a Zod schema?**
 *   - Listeners that mutate `financialsSnapshot` (multiple services
 *     write here) have to agree on the shape, or one writer's update
 *     silently corrupts another's. A schema gives us a runtime check at
 *     the boundary.
 *   - The audit redactor's sensitive-field list previously had to be
 *     hand-mirrored against this shape. Exporting
 *     `FINANCIALS_PROVENANCE_SENSITIVE_FIELDS` from this module and
 *     spreading it into the redactor means adding a new sensitive key
 *     to the schema auto-updates the redactor — no second commit, no
 *     drift window.
 */

import { z } from 'zod';

/**
 * Catalogue of sources that may write to a borrower's financials
 * snapshot. Open-ended on purpose — `manual_edit` covers any operator
 * action that doesn't have its own listener channel yet.
 */
export const financialsProvenanceSourceSchema = z.enum([
  'credit_report_extraction',
  'pfs_extraction',
  'borrower_exit_close',
  'manual_edit',
]);

export type FinancialsProvenanceSource = z.infer<typeof financialsProvenanceSourceSchema>;

/**
 * One entry in `financialsSnapshot.sources[]`. Records WHO updated WHICH
 * field WHEN, so audits and debugging can trace any value back to its
 * origin without scanning every event log.
 */
export const financialsProvenanceEntrySchema = z.object({
  /**
   * The snapshot field whose value this provenance entry describes.
   * Typically one of `creditScore`, `liquidity`, `netWorth`,
   * `exitCount36mo`, `backgroundReportFlagged` — but kept open so new
   * fields can be tracked without a schema bump.
   */
  field: z.string().min(1),
  /** Where the new value came from. */
  source: financialsProvenanceSourceSchema,
  /**
   * Identifier inside the source system — documentId for an extraction,
   * exitId for an exit close, userId for a manual edit. Optional because
   * not every legacy entry carries it.
   */
  sourceId: z.string().optional(),
  /** ISO-8601 timestamp at which this entry was written. */
  at: z.string().datetime({ offset: true }),
});

export type FinancialsProvenanceEntry = z.infer<typeof financialsProvenanceEntrySchema>;

/**
 * Schema for `BorrowingEntity.financialsSnapshot`. Every key is optional
 * because listeners merge field-by-field — a single source's update must
 * never blow away another source's previously-recorded fields.
 */
export const financialsSnapshotSchema = z
  .object({
    /** FICO score. Sensitive; redacted in audit logs. */
    creditScore: z.number().int().nonnegative().optional(),
    /** Liquid reserves $. Sensitive; redacted in audit logs. */
    liquidity: z.number().nonnegative().optional(),
    /** Net worth $. Sensitive; redacted in audit logs. */
    netWorth: z.number().optional(),
    /** Trailing-36-month closed exit count (generic — sum of reno + construction). */
    exitCount36mo: z.number().int().nonnegative().optional(),
    /**
     * Trailing-36-month closed renovation/fix-and-flip exit count
     * (1Sharpe warehouse-line — ties renovation experience to fix-and-flip
     * tier qualification separately from construction experience).
     */
    renoExitCount36mo: z.number().int().nonnegative().optional(),
    /**
     * Trailing-36-month closed ground-up construction exit count
     * (1Sharpe warehouse-line — separate qualification track from renovation).
     */
    constructionExitCount36mo: z.number().int().nonnegative().optional(),
    /**
     * Whether the background report surfaced concerning items. Boolean
     * (not a flag count) so consumers don't try to do arithmetic with
     * the value.
     */
    backgroundReportFlagged: z.boolean().optional(),
    /**
     * Most recent bankruptcy discharge date. NULL = no BK on record.
     * Used by BorrowerTierService with rule `bkAgeYearsMin` to evaluate
     * "BK must be 7+ years old" gates (1Sharpe T0-T2).
     */
    bankruptcyDischargeDate: z.string().datetime({ offset: true }).optional(),
    /**
     * Most recent foreclosure date. NULL = no foreclosure on record.
     * Used by BorrowerTierService with rule `foreclosureAgeYearsMin`.
     */
    foreclosureDate: z.string().datetime({ offset: true }).optional(),
    /**
     * Whether the borrower has outstanding liens or judgments. Default
     * (when absent from snapshot) is `false` — clean. Rules with
     * `requireNoLiensOutstanding=true` reject when this is `true`.
     */
    hasOutstandingLiens: z.boolean().optional(),
    /**
     * Whether the borrower is a foreign national. Triggers WL-level
     * leverage haircut and desk-review gates per
     * `WarehouseLineCreditBoxPolicy.foreignNational*`.
     */
    isForeignNational: z.boolean().optional(),
    /** ISO-8601 timestamp of the most recent field update. */
    lastUpdatedAt: z.string().datetime({ offset: true }).optional(),
    /** Per-field provenance trail. */
    sources: z.array(financialsProvenanceEntrySchema).optional(),
  })
  // Snapshots are merged by listeners — accept extra fields rather than
  // strip them. A future field added by service A shouldn't be wiped by
  // service B that doesn't know about it yet.
  .passthrough();

export type FinancialsSnapshot = z.infer<typeof financialsSnapshotSchema>;

/**
 * Schema for the per-guarantor `inputs` block inside
 * `Case.tierReasonContext.guarantorTiers[].inputs`. Captures the raw
 * underwriting signals the classifier saw — distinct from the snapshot
 * because:
 *
 *   - The snapshot is mutable (updated by listeners over time).
 *   - The classifier inputs are an immutable photograph at classify
 *     time — kept verbatim for "why is this borrower Tier 3"
 *     reproducibility.
 *
 * Field names are renamed (`liquidReserves` not `liquidity`) on purpose
 * — the classifier is fed a slightly different vocabulary than the
 * snapshot stores, and we don't want to muddle the boundary.
 */
export const guarantorTierInputsSchema = z
  .object({
    creditScore: z.number().int().nonnegative().optional(),
    liquidReserves: z.number().nonnegative().optional(),
    exitCount36mo: z.number().int().nonnegative().optional(),
  })
  .passthrough();

export type GuarantorTierInputs = z.infer<typeof guarantorTierInputsSchema>;

/**
 * One guarantor's classification entry inside
 * `Case.tierReasonContext.guarantorTiers[]`. Either `consumerId` or
 * `entityId` is populated (Issue #636 — guarantors can be individual
 * consumers or borrowing entities).
 */
export const guarantorTierEntrySchema = z.object({
  /** Set when the guarantor is an individual consumer. */
  consumerId: z.string().optional(),
  /** Set when the guarantor is a borrowing entity. */
  entityId: z.string().optional(),
  /** The tier the classifier assigned this guarantor (1, 2, or 3). */
  // Tier domain relaxed to 1..9 (1Sharpe warehouse-line follow-up) — 1 is
  // always "best". The upper bound is conservative; warehouse-line
  // conventions that ship more than 3 tiers (1Sharpe T0-T3) fit
  // comfortably below 9.
  tier: z.number().int().min(1).max(9),
  /**
   * The borrower-tier-rule id that fired. `null` when no rule matched
   * and the classifier fell back to Tier 3.
   */
  matchedRuleId: z.string().nullable(),
  /** Raw underwriting signals the classifier saw — sensitive. */
  inputs: guarantorTierInputsSchema,
});

export type GuarantorTierEntry = z.infer<typeof guarantorTierEntrySchema>;

/**
 * Schema for `Case.tierReasonContext`. Captures the explanation of how
 * the case landed at its effective tier so the UI can render a
 * "Tier 3 because guarantor X is Tier 3" breakdown.
 */
export const tierReasonContextSchema = z.object({
  /** The consumer whose tier became the case's effective tier. */
  deciderConsumerId: z.string().optional(),
  /** The entity whose tier became the case's effective tier. */
  deciderEntityId: z.string().optional(),
  /** The chosen effective tier (1..9, 1 = best). */
  deciderTier: z.number().int().min(1).max(9),
  /** Per-guarantor breakdown — empty when no guarantors are on the case. */
  guarantorTiers: z.array(guarantorTierEntrySchema),
  /**
   * Which decision rule produced the effective tier:
   *   - `LOWEST_AMONG_GUARANTORS` — pick the worst (highest tier number)
   *     across guarantors.
   *   - `CASE_METADATA_FALLBACK`  — no guarantors with snapshots, so the
   *     classifier fell back to v1 (read tier from case.metadata).
   */
  ruleApplied: z.enum(['LOWEST_AMONG_GUARANTORS', 'CASE_METADATA_FALLBACK']),
  /** ISO-8601 timestamp at which the classifier ran. */
  evaluatedAt: z.string().datetime({ offset: true }),
});

export type TierReasonContext = z.infer<typeof tierReasonContextSchema>;

/**
 * Top-level provenance metadata that wraps a payload of underwriting
 * signals (used by writers that want to attach a single source label
 * to a multi-field update without per-field provenance entries).
 *
 * Distinct from `financialsProvenanceEntrySchema` (per-field) — this
 * schema describes "this entire payload was extracted at this time
 * from this source".
 */
export const financialsProvenanceMetadataSchema = z.object({
  /** ISO-8601 timestamp at which this payload was generated. */
  asOf: z.string().datetime({ offset: true }),
  /** Where the payload came from. */
  source: financialsProvenanceSourceSchema,
  /** Optional identifier inside that source (documentId, exitId, ...). */
  sourceId: z.string().optional(),
});

export type FinancialsProvenanceMetadata = z.infer<typeof financialsProvenanceMetadataSchema>;

/**
 * Combined "provenance" union schema covering the two columns this
 * module is the source of truth for:
 *   - `BorrowingEntity.financialsSnapshot`
 *   - `Case.tierReasonContext`
 *
 * Useful when a generic boundary (RPC, audit emitter) needs to validate
 * an inbound payload that could be either shape.
 */
export const financialsProvenanceSchema = z.union([financialsSnapshotSchema, tierReasonContextSchema]);

export type FinancialsProvenance = z.infer<typeof financialsProvenanceSchema>;

/**
 * Field names from this module's schemas that contain borrower PII and
 * MUST be redacted in audit / observability payloads.
 *
 * **This is the single source of truth.** The audit redactor in
 * `audit/audit-utils.ts` imports this constant directly — adding a new
 * sensitive key to the schema means adding it here, and the redactor
 * picks it up automatically.
 *
 * Includes both snake_case and camelCase variants because the audit
 * sanitiser also matches `tier_reason_context` JSON dumps coming from
 * raw Postgres rows where keys arrive in snake_case.
 */
export const FINANCIALS_PROVENANCE_SENSITIVE_FIELDS = [
  // Per-snapshot signals (BorrowingEntity.financialsSnapshot)
  'creditScore',
  'credit_score',
  'liquidity',
  'liquidReserves',
  'liquid_reserves',
  'netWorth',
  'net_worth',
  // Per-guarantor borrower signals (Case.tierReasonContext.guarantorTiers[].inputs)
  // These overlap with snapshot fields but are spelled differently in
  // the classifier inputs — list both to keep redaction air-tight
  // regardless of which surface the payload came from.
  'borrowerCredit',
  'borrowerLiquidity',
  'borrowerNetWorth',
  'fico',
  'ficoScore',
  'fico_score',
  // The free-form tier reason string itself echoes FICO / liquidity
  // numerals back into the audit payload, so it's redacted too.
  'tierReason',
  'tier_reason',
  // 1Sharpe warehouse-line — borrower-sensitive history added to the
  // snapshot for fine-grained qualification gates. BK/foreclosure dates,
  // lien status, and citizenship status are all PII; the separate
  // reno/construction exit counts can be cross-referenced against a
  // public deed search to identify the borrower, so they're treated as
  // PII alongside the existing exit-count generic.
  'bankruptcyDischargeDate',
  'bankruptcy_discharge_date',
  'foreclosureDate',
  'foreclosure_date',
  'hasOutstandingLiens',
  'has_outstanding_liens',
  'isForeignNational',
  'is_foreign_national',
  'renoExitCount36mo',
  'reno_exit_count_36mo',
  'constructionExitCount36mo',
  'construction_exit_count_36mo',
] as const;

/**
 * Type-side of `FINANCIALS_PROVENANCE_SENSITIVE_FIELDS` — the union of
 * every literal in the readonly tuple.
 */
export type FinancialsProvenanceSensitiveField = (typeof FINANCIALS_PROVENANCE_SENSITIVE_FIELDS)[number];
